import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTranslations } from '../../contexts/TranslationContext';
import { formatCategoryName } from '../../utils/categoryFormatter';

// Import shared categories at the top of the file
import categoriesData from '../../backendshared/xnews_categories.json';

// Add type definition for categories
interface Categories {
  main: string[];
  [key: string]: string[]; // All other category properties are string arrays
}

// Type assertion for categories
const categories = categoriesData as Categories;

// Interface for category items with key and display properties
interface CategoryItem {
  key: string;
  display: string;
}

interface HeaderPageProps {
  locale?: string;
  setLocale?: (loc: string) => void;
}

// Create a cache for transformed categories
interface CategoryCache {
  [key: string]: CategoryItem[];
}

const Header: React.FC<HeaderPageProps> = ({ locale: propLocale, setLocale: propSetLocale }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const entertainmentRef = useRef<HTMLLIElement>(null);
  const entertainmentMenuRef = useRef<HTMLUListElement>(null);
  const [isEntertainmentOpen, setIsEntertainmentOpen] = useState(false);
  const newsRef = useRef<HTMLLIElement>(null);
  const newsMenuRef = useRef<HTMLUListElement>(null);
  const [isNewsOpen, setIsNewsOpen] = useState(false);
  const sportsRef = useRef<HTMLLIElement>(null);
  const sportsMenuRef = useRef<HTMLUListElement>(null);
  const [isSportsOpen, setIsSportsOpen] = useState(false);
  const trendingRef = useRef<HTMLLIElement>(null);
  const trendingMenuRef = useRef<HTMLUListElement>(null);
  const [isTrendingOpen, setIsTrendingOpen] = useState(false);

  // Add refs and state for category dropdowns
  const worldRef = useRef<HTMLLIElement>(null);
  const worldMenuRef = useRef<HTMLUListElement>(null);
  const [isWorldOpen, setIsWorldOpen] = useState(false);

  const politicsRef = useRef<HTMLLIElement>(null);
  const politicsMenuRef = useRef<HTMLUListElement>(null);
  const [isPoliticsOpen, setIsPoliticsOpen] = useState(false);

  const businessRef = useRef<HTMLLIElement>(null);
  const businessMenuRef = useRef<HTMLUListElement>(null);
  const [isBusinessOpen, setIsBusinessOpen] = useState(false);

  const technologyRef = useRef<HTMLLIElement>(null);
  const technologyMenuRef = useRef<HTMLUListElement>(null);
  const [isTechnologyOpen, setIsTechnologyOpen] = useState(false);

  const healthRef = useRef<HTMLLIElement>(null);
  const healthMenuRef = useRef<HTMLUListElement>(null);
  const [isHealthOpen, setIsHealthOpen] = useState(false);

  const lifestyleRef = useRef<HTMLLIElement>(null);
  const lifestyleMenuRef = useRef<HTMLUListElement>(null);
  const [isLifestyleOpen, setIsLifestyleOpen] = useState(false);

  // Add state and ref for More dropdown
  const moreRef = useRef<HTMLLIElement>(null);
  const moreMenuRef = useRef<HTMLUListElement>(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  // Track which subtype dropdown is open within the More dropdown
  const [activeMoreSubmenu, setActiveMoreSubmenu] = useState<string | null>(null);

  const navigate = useNavigate();

  // Use the TranslationContext for translations, language and changing language
  const {
    translations: t,
    isLoading,
    getUrlLanguagePrefix,
    language,
    changeLanguage
  } = useTranslations();

  // Use the language directly from TranslationContext
  const currentLang = language;

  console.log('[HEADER] Rendering with language:', currentLang);
  console.log('[HEADER] Has translations:', !!t, 'Has header:', !!t?.navigations?.header);

  // Add a categories cache reference to persist between renders
  const categoriesCache = useRef<CategoryCache>({});

  // Transform string arrays to required format with key and display properties
  // Memoize and cache the transformCategories function to prevent redundant operations
  const transformCategories = useCallback((categoryArray: string[] = [], cacheKey: string): CategoryItem[] => {
    // Check if we already have this category transformed and cached
    if (categoriesCache.current[cacheKey]) {
      return categoriesCache.current[cacheKey];
    }

    // If in development, log only the first time we transform a category
    if (process.env.NODE_ENV === 'development') {
      console.log('[HEADER] Transforming categories:', {
        categoryCount: categoryArray.length,
        sampleCategories: categoryArray.slice(0, 3),
        hasTranslations: !!t?.navigations?.header,
        availableKeys: t?.navigations?.header ? Object.keys(t.navigations.header).slice(0, 10) : []
      });
    }

    const transformedCategories = categoryArray.map(item => {
      const itemLower = item.toLowerCase();
      const itemUpperFirst = item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();

      // Try different case formats for finding translations
      const fromLowercase = t?.navigations?.header?.[itemLower];
      const fromOriginal = t?.navigations?.header?.[item];
      const fromUpperFirst = t?.navigations?.header?.[itemUpperFirst];

      // Format using the utility function
      const formattedText = formatCategoryName(item);

      // Only log missing translations if we have header translations but none for this category
      // This prevents false warnings when we have properly formatted category names
      if (process.env.NODE_ENV === 'development' &&
        t?.navigations?.header &&
        Object.keys(t.navigations.header).length > 0 &&
        !fromLowercase && !fromOriginal && !fromUpperFirst) {
        // Use formattedText as fallback but prioritize translations when available
        console.log(`[HEADER] Missing translation for category: ${item}`);
      }

      return {
        key: item,
        display: fromLowercase || fromOriginal || fromUpperFirst || formattedText
      };
    });

    // Cache the result
    categoriesCache.current[cacheKey] = transformedCategories;

    return transformedCategories;
  }, [t?.navigations?.header]);

  // Clear cache when language changes
  useEffect(() => {
    if (categoriesCache.current && Object.keys(categoriesCache.current).length > 0) {
      console.log('[HEADER] Clearing categories cache due to language change');
      categoriesCache.current = {};
    }
  }, [currentLang]);

  // Use memoized values for all categories with proper cache keys
  const entertainmentCategories = useMemo(() =>
    transformCategories(categories.entertainment, `entertainment-${currentLang}`),
    [categories.entertainment, transformCategories, currentLang]
  );

  const trendingCategories = useMemo(() =>
    transformCategories(categories.trending, `trending-${currentLang}`),
    [categories.trending, transformCategories, currentLang]
  );

  const sportsCategories = useMemo(() =>
    transformCategories(categories.sports, `sports-${currentLang}`),
    [categories.sports, transformCategories, currentLang]
  );

  const worldSubtypes = useMemo(() =>
    transformCategories(categories.world, `world-${currentLang}`),
    [categories.world, transformCategories, currentLang]
  );

  const politicsSubtypes = useMemo(() =>
    transformCategories(categories.politics, `politics-${currentLang}`),
    [categories.politics, transformCategories, currentLang]
  );

  const businessSubtypes = useMemo(() =>
    transformCategories(categories.business, `business-${currentLang}`),
    [categories.business, transformCategories, currentLang]
  );

  const technologySubtypes = useMemo(() =>
    transformCategories(categories.technology, `technology-${currentLang}`),
    [categories.technology, transformCategories, currentLang]
  );

  const healthSubtypes = useMemo(() =>
    transformCategories(categories.health, `health-${currentLang}`),
    [categories.health, transformCategories, currentLang]
  );

  const lifestyleSubtypes = useMemo(() =>
    transformCategories(categories.lifestyle, `lifestyle-${currentLang}`),
    [categories.lifestyle, transformCategories, currentLang]
  );

  const handleLinkClick = (path: string, queryParams?: Record<string, string>) => {
    const languagePrefix = getUrlLanguagePrefix(currentLang);
    let fullPath = `${languagePrefix}${path}`;

    console.log(`[HEADER] Navigating to: ${fullPath} with language ${currentLang}`);

    // Add query parameters if provided
    if (queryParams && Object.keys(queryParams).length > 0) {
      const queryString = Object.entries(queryParams)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
      fullPath += `?${queryString}`;
    }

    navigate(fullPath);
    setIsOpen(false);
    setIsEntertainmentOpen(false);
    setIsNewsOpen(false);
    setIsSportsOpen(false);
    setIsTrendingOpen(false);
    setIsWorldOpen(false);
    setIsPoliticsOpen(false);
    setIsBusinessOpen(false);
    setIsTechnologyOpen(false);
    setIsHealthOpen(false);
    setIsLifestyleOpen(false);
  };

  // Always use these languages
  const languages = [
    { code: 'en', display: 'English' },
    { code: 'cn', display: '简体中文' },
    { code: 'tw', display: '繁體中文' },
    { code: 'es', display: 'Español' },
    { code: 'fr', display: 'Français' }
  ];

  // Get the current language to display in the dropdown - memoize to prevent unnecessary calculations
  const currentLanguage = useMemo(() => {
    console.log(`[HEADER] Calculating current language display for: ${currentLang}`);

    // Exact matches first
    if (currentLang === 'en') return languages[0];
    if (currentLang === 'cn') return languages[1];
    if (currentLang === 'tw') return languages[2];
    if (currentLang === 'es') return languages[3];
    if (currentLang === 'fr') return languages[4];

    // Then partial matches
    const currentLangLower = currentLang.toLowerCase();
    if (currentLangLower.startsWith('en')) return languages[0];
    if (currentLangLower.startsWith('cn')) return languages[1];
    if (currentLangLower.startsWith('tw')) return languages[2];
    if (currentLangLower.startsWith('es')) return languages[3];
    if (currentLangLower.startsWith('fr')) return languages[4];

    // Default to English
    return languages[0];
  }, [currentLang]);

  const handleLanguageSwitch = (newLocale: string) => {
    console.log(`[HEADER] handleLanguageSwitch called with locale: "${newLocale}"`);

    // Skip if the locale is the same as current
    if (currentLang === newLocale) {
      console.log(`[HEADER] Already using language ${newLocale}, skipping language switch`);
      setIsLanguageOpen(false);
      return;
    }

    // Close the language dropdown immediately
    setIsLanguageOpen(false);

    // Show a loading indicator to the user to provide feedback
    // that something is happening
    if (isLoading) {
      console.log(`[HEADER] Language switch already in progress, waiting...`);
    }

    // Use the TranslationContext's changeLanguage method directly
    // This will handle both the API call and navigation
    changeLanguage(newLocale)
      .then(success => {
        console.log(`[HEADER] Language change to ${newLocale} completed with status: ${success}`);

        // Update parent locale if needed (for backward compatibility)
        if (propSetLocale) {
          console.log(`[HEADER] Calling parent setLocale with ${newLocale}`);
          propSetLocale(newLocale);
        }
      })
      .catch(error => {
        console.error(`[HEADER] Error changing language:`, error);
      });
  };

  // Show all languages except the current one
  const filteredLanguages = useMemo(() =>
    languages.filter(lang => lang.code !== currentLanguage.code),
    [currentLanguage.code]
  );

  // Add refs for language dropdown
  const languageRef = useRef<HTMLLIElement>(null);
  const languageMenuRef = useRef<HTMLUListElement>(null);

  // Handle outside clicks to close the entertainment, news, and sports dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // For mobile: Close all dropdowns when tapping elsewhere on the screen
      if (window.innerWidth < 768) {
        const isClickingOnMenu =
          newsRef.current?.contains(event.target as Node) ||
          newsMenuRef.current?.contains(event.target as Node) ||
          entertainmentRef.current?.contains(event.target as Node) ||
          entertainmentMenuRef.current?.contains(event.target as Node) ||
          sportsRef.current?.contains(event.target as Node) ||
          sportsMenuRef.current?.contains(event.target as Node) ||
          languageRef.current?.contains(event.target as Node) ||
          languageMenuRef.current?.contains(event.target as Node) ||
          trendingRef.current?.contains(event.target as Node) ||
          trendingMenuRef.current?.contains(event.target as Node) ||
          worldRef.current?.contains(event.target as Node) ||
          worldMenuRef.current?.contains(event.target as Node) ||
          politicsRef.current?.contains(event.target as Node) ||
          politicsMenuRef.current?.contains(event.target as Node) ||
          businessRef.current?.contains(event.target as Node) ||
          businessMenuRef.current?.contains(event.target as Node) ||
          technologyRef.current?.contains(event.target as Node) ||
          technologyMenuRef.current?.contains(event.target as Node) ||
          healthRef.current?.contains(event.target as Node) ||
          healthMenuRef.current?.contains(event.target as Node) ||
          lifestyleRef.current?.contains(event.target as Node) ||
          lifestyleMenuRef.current?.contains(event.target as Node) ||
          moreRef.current?.contains(event.target as Node) ||
          moreMenuRef.current?.contains(event.target as Node);

        if (!isClickingOnMenu) {
          setIsNewsOpen(false);
          setIsEntertainmentOpen(false);
          setIsSportsOpen(false);
          setIsLanguageOpen(false);
          setIsTrendingOpen(false);
          setIsWorldOpen(false);
          setIsPoliticsOpen(false);
          setIsBusinessOpen(false);
          setIsTechnologyOpen(false);
          setIsHealthOpen(false);
          setIsLifestyleOpen(false);
          setIsMoreOpen(false);
          return;
        }
      }

      // Check entertainment dropdown
      if (
        isEntertainmentOpen &&
        entertainmentRef.current &&
        !entertainmentRef.current.contains(event.target as Node) &&
        entertainmentMenuRef.current &&
        !entertainmentMenuRef.current.contains(event.target as Node)
      ) {
        setIsEntertainmentOpen(false);
      }

      // Check news dropdown
      if (
        isNewsOpen &&
        newsRef.current &&
        !newsRef.current.contains(event.target as Node) &&
        newsMenuRef.current &&
        !newsMenuRef.current.contains(event.target as Node)
      ) {
        setIsNewsOpen(false);
      }

      // Check sports dropdown
      if (
        isSportsOpen &&
        sportsRef.current &&
        !sportsRef.current.contains(event.target as Node) &&
        sportsMenuRef.current &&
        !sportsMenuRef.current.contains(event.target as Node)
      ) {
        setIsSportsOpen(false);
      }

      // Check language dropdown
      if (
        isLanguageOpen &&
        languageRef.current &&
        !languageRef.current.contains(event.target as Node) &&
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target as Node)
      ) {
        setIsLanguageOpen(false);
      }

      // Check trending dropdown
      if (
        isTrendingOpen &&
        trendingRef.current &&
        !trendingRef.current.contains(event.target as Node) &&
        trendingMenuRef.current &&
        !trendingMenuRef.current.contains(event.target as Node)
      ) {
        setIsTrendingOpen(false);
      }

      // Check world dropdown
      if (
        isWorldOpen &&
        worldRef.current &&
        !worldRef.current.contains(event.target as Node) &&
        worldMenuRef.current &&
        !worldMenuRef.current.contains(event.target as Node)
      ) {
        setIsWorldOpen(false);
      }

      // Check politics dropdown
      if (
        isPoliticsOpen &&
        politicsRef.current &&
        !politicsRef.current.contains(event.target as Node) &&
        politicsMenuRef.current &&
        !politicsMenuRef.current.contains(event.target as Node)
      ) {
        setIsPoliticsOpen(false);
      }

      // Check business dropdown
      if (
        isBusinessOpen &&
        businessRef.current &&
        !businessRef.current.contains(event.target as Node) &&
        businessMenuRef.current &&
        !businessMenuRef.current.contains(event.target as Node)
      ) {
        setIsBusinessOpen(false);
      }

      // Check technology dropdown
      if (
        isTechnologyOpen &&
        technologyRef.current &&
        !technologyRef.current.contains(event.target as Node) &&
        technologyMenuRef.current &&
        !technologyMenuRef.current.contains(event.target as Node)
      ) {
        setIsTechnologyOpen(false);
      }

      // Check health dropdown
      if (
        isHealthOpen &&
        healthRef.current &&
        !healthRef.current.contains(event.target as Node) &&
        healthMenuRef.current &&
        !healthMenuRef.current.contains(event.target as Node)
      ) {
        setIsHealthOpen(false);
      }

      // Check lifestyle dropdown
      if (
        isLifestyleOpen &&
        lifestyleRef.current &&
        !lifestyleRef.current.contains(event.target as Node) &&
        lifestyleMenuRef.current &&
        !lifestyleMenuRef.current.contains(event.target as Node)
      ) {
        setIsLifestyleOpen(false);
      }

      // Check more dropdown
      if (
        isMoreOpen &&
        moreRef.current &&
        !moreRef.current.contains(event.target as Node) &&
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target as Node)
      ) {
        setIsMoreOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEntertainmentOpen, isNewsOpen, isSportsOpen, isLanguageOpen, isTrendingOpen, isWorldOpen, isPoliticsOpen, isBusinessOpen, isTechnologyOpen, isHealthOpen, isLifestyleOpen, isMoreOpen]);

  // Only show loading when we have no translations at all
  if (isLoading && (!t || !t.navigations)) {
    return <div className="bg-black p-4 shadow-md">Loading...</div>;
  }

  // Add dropdown category type
  type DropdownCategory = 'news' | 'entertainment' | 'sports' | 'language' | 'trending' | 'world' | 'politics' | 'business' | 'technology' | 'health' | 'lifestyle' | 'more';

  const toggleDropdown = (key: DropdownCategory) => {
    console.log(`[HEADER] Toggling dropdown for ${key}`);

    // Close all other dropdowns first to prevent multiple open dropdowns
    if (key !== 'news') setIsNewsOpen(false);
    if (key !== 'entertainment') setIsEntertainmentOpen(false);
    if (key !== 'sports') setIsSportsOpen(false);
    if (key !== 'language') setIsLanguageOpen(false);
    if (key !== 'trending') setIsTrendingOpen(false);
    if (key !== 'world') setIsWorldOpen(false);
    if (key !== 'politics') setIsPoliticsOpen(false);
    if (key !== 'business') setIsBusinessOpen(false);
    if (key !== 'technology') setIsTechnologyOpen(false);
    if (key !== 'health') setIsHealthOpen(false);
    if (key !== 'lifestyle') setIsLifestyleOpen(false);
    if (key !== 'more') setIsMoreOpen(false);

    // Then toggle the requested dropdown
    if (key === 'news') setIsNewsOpen(prev => !prev);
    else if (key === 'entertainment') setIsEntertainmentOpen(prev => !prev);
    else if (key === 'sports') setIsSportsOpen(prev => !prev);
    else if (key === 'language') setIsLanguageOpen(prev => !prev);
    else if (key === 'trending') setIsTrendingOpen(prev => !prev);
    else if (key === 'world') setIsWorldOpen(prev => !prev);
    else if (key === 'politics') setIsPoliticsOpen(prev => !prev);
    else if (key === 'business') setIsBusinessOpen(prev => !prev);
    else if (key === 'technology') setIsTechnologyOpen(prev => !prev);
    else if (key === 'health') setIsHealthOpen(prev => !prev);
    else if (key === 'lifestyle') setIsLifestyleOpen(prev => !prev);
    else if (key === 'more') setIsMoreOpen(prev => !prev);
  };

  const handleMouseEnter = (key: DropdownCategory) => {
    // Only use hover on desktop devices (larger than tablets)
    if (window.innerWidth < 1024) return;

    if (key === 'news') setIsNewsOpen(true);
    else if (key === 'entertainment') setIsEntertainmentOpen(true);
    else if (key === 'sports') setIsSportsOpen(true);
    else if (key === 'language') setIsLanguageOpen(true);
    else if (key === 'trending') setIsTrendingOpen(true);
    else if (key === 'world') setIsWorldOpen(true);
    else if (key === 'politics') setIsPoliticsOpen(true);
    else if (key === 'business') setIsBusinessOpen(true);
    else if (key === 'technology') setIsTechnologyOpen(true);
    else if (key === 'health') setIsHealthOpen(true);
    else if (key === 'lifestyle') setIsLifestyleOpen(true);
    else if (key === 'more') setIsMoreOpen(true);
  };

  const handleMouseLeave = (key: DropdownCategory) => {
    // Only use hover on desktop devices (larger than tablets)
    if (window.innerWidth < 1024) return;

    // Add small delay before closing dropdowns
    setTimeout(() => {
      if (key === 'news') setIsNewsOpen(false);
      else if (key === 'entertainment') setIsEntertainmentOpen(false);
      else if (key === 'sports') setIsSportsOpen(false);
      else if (key === 'language') setIsLanguageOpen(false);
      else if (key === 'trending') setIsTrendingOpen(false);
      else if (key === 'world') setIsWorldOpen(false);
      else if (key === 'politics') setIsPoliticsOpen(false);
      else if (key === 'business') setIsBusinessOpen(false);
      else if (key === 'technology') setIsTechnologyOpen(false);
      else if (key === 'health') setIsHealthOpen(false);
      else if (key === 'lifestyle') setIsLifestyleOpen(false);
      else if (key === 'more') setIsMoreOpen(false);
    }, 150);
  };

  // Add CSS to create a smooth hover experience with a brief delay
  useEffect(() => {
    // Create a style element
    const style = document.createElement('style');

    // Add the CSS rules
    style.innerHTML = `
      @media (min-width: 1024px) {
        /* Create a slightly delayed close of dropdown menus */
        .news-dropdown, .entertainment-dropdown, .sports-dropdown, .language-dropdown, .trending-dropdown, 
        .world-dropdown, .politics-dropdown, .business-dropdown, .technology-dropdown, .health-dropdown, .lifestyle-dropdown {
          transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
        }
        
        /* Add invisible connector between button and dropdown to prevent accidental hover loss */
        .dropdown-connector {
          position: absolute;
          height: 10px;
          left: 0;
          right: 0;
          bottom: -10px;
          z-index: 10;
        }
      }
    `;

    // Add the style to the document
    document.head.appendChild(style);

    // Clean up function
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Add touch event listeners for iOS/iPad
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      // For debugging
      console.log('[HEADER] Touch start detected');

      // Check if touch is outside all dropdowns and close them if needed
      const isOutsideAll = ![
        newsRef.current,
        newsMenuRef.current,
        entertainmentRef.current,
        entertainmentMenuRef.current,
        sportsRef.current,
        sportsMenuRef.current,
        languageRef.current,
        languageMenuRef.current,
        trendingRef.current,
        trendingMenuRef.current,
        worldRef.current,
        worldMenuRef.current,
        politicsRef.current,
        politicsMenuRef.current,
        businessRef.current,
        businessMenuRef.current,
        technologyRef.current,
        technologyMenuRef.current,
        healthRef.current,
        healthMenuRef.current,
        lifestyleRef.current,
        lifestyleMenuRef.current,
        moreRef.current,
        moreMenuRef.current
      ].some(ref => ref && ref.contains(e.target as Node));

      // Close all dropdowns if clicking outside
      if (isOutsideAll) {
        console.log('[HEADER] Touch outside all dropdowns, closing all');
        setIsNewsOpen(false);
        setIsEntertainmentOpen(false);
        setIsSportsOpen(false);
        setIsLanguageOpen(false);
        setIsTrendingOpen(false);
        setIsWorldOpen(false);
        setIsPoliticsOpen(false);
        setIsBusinessOpen(false);
        setIsTechnologyOpen(false);
        setIsHealthOpen(false);
        setIsLifestyleOpen(false);
        setIsMoreOpen(false);
      }
    };

    // Add event listener for touch events
    document.addEventListener('touchstart', handleTouchStart, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, []);

  // Handle all click/touch events globally
  useEffect(() => {
    // Combined function to handle both touch and click events
    const handleInteraction = (event: MouseEvent | TouchEvent) => {
      console.log('[HEADER] Interaction event detected:', event.type);

      const target = event.target as Node;

      // Special handling for iPad simulator
      const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
        ('ontouchend' in document || navigator.maxTouchPoints > 0);

      if (isIpadSimulator) {
        console.log('[HEADER] iPad simulator detected');
      }

      // Check if interaction is outside all dropdowns
      const isOutsideAll = ![
        newsRef.current,
        newsMenuRef.current,
        entertainmentRef.current,
        entertainmentMenuRef.current,
        sportsRef.current,
        sportsMenuRef.current,
        languageRef.current,
        languageMenuRef.current,
        trendingRef.current,
        trendingMenuRef.current,
        worldRef.current,
        worldMenuRef.current,
        politicsRef.current,
        politicsMenuRef.current,
        businessRef.current,
        businessMenuRef.current,
        technologyRef.current,
        technologyMenuRef.current,
        healthRef.current,
        healthMenuRef.current,
        lifestyleRef.current,
        lifestyleMenuRef.current,
        moreRef.current,
        moreMenuRef.current
      ].some(ref => ref && ref.contains(target));

      // If clicking/touching outside all dropdowns, close them all
      if (isOutsideAll) {
        console.log('[HEADER] Interaction outside all dropdowns, closing all');
        setIsNewsOpen(false);
        setIsEntertainmentOpen(false);
        setIsSportsOpen(false);
        setIsLanguageOpen(false);
        setIsTrendingOpen(false);
        setIsWorldOpen(false);
        setIsPoliticsOpen(false);
        setIsBusinessOpen(false);
        setIsTechnologyOpen(false);
        setIsHealthOpen(false);
        setIsLifestyleOpen(false);
        setIsMoreOpen(false);
      }
    };

    // Add event listeners for both types of events
    document.addEventListener('mousedown', handleInteraction);
    document.addEventListener('touchstart', handleInteraction, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  // Add iPad simulator specific CSS
  useEffect(() => {
    // Create a style element for iPad specific fixes
    const style = document.createElement('style');

    // Add iPad specific CSS rules
    style.innerHTML = `
      @media (min-width: 740px) and (max-width: 1024px) {
        /* Global container adjustments */
        .container {
          padding-left: 0.5rem !important;
          padding-right: 0.5rem !important;
          max-width: 100% !important;
        }

        /* Ensure header is properly positioned */
        .header-container {
          position: relative;
          width: 100%;
        }

        /* Adjust logo size and spacing */
        .logo-button {
          font-size: 1.5rem;
          padding: 0.5rem 1rem;
        }

        /* Ensure navigation menu is properly positioned */
        .nav-menu {
          position: relative;
          width: auto;
          background: transparent;
          display: flex !important;
          justify-content: space-between;
        }

        /* Adjust dropdown positioning and appearance */
        .news-dropdown-menu, .entertainment-dropdown-menu, .sports-dropdown-menu, .language-dropdown-menu, .trending-dropdown-menu,
        .world-dropdown-menu, .politics-dropdown-menu, .business-dropdown-menu, .technology-dropdown-menu, .health-dropdown-menu, .lifestyle-dropdown-menu {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          min-width: 200px;
          background: black;
          border: 1px solid #333;
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          z-index: 1000;
        }

        /* Position each dropdown menu separately */
        .news-dropdown-menu {
          left: 0;
          transform: none;
        }

        .entertainment-dropdown-menu {
          left: 50%;
          transform: translateX(-50%);
        }

        .sports-dropdown-menu {
          left: auto;
          right: 50%;
          transform: translateX(50%);
        }

        .language-dropdown-menu {
          left: auto;
          right: 0;
          transform: none;
        }

        .trending-dropdown-menu {
          left: auto;
          right: 0;
          transform: none;
        }

        .world-dropdown-menu, .politics-dropdown-menu, .business-dropdown-menu, 
        .technology-dropdown-menu, .health-dropdown-menu, .lifestyle-dropdown-menu {
          left: 50%;
          transform: translateX(-50%);
        }

        /* Ensure dropdown buttons are properly spaced */
        .nav-item {
          position: relative;
          padding: 0.5rem 0.5rem;
          margin: 0;
        }

        /* Ensure text is properly sized and spaced */
        .nav-text {
          font-size: 0.9rem;
          line-height: 1.4;
          white-space: nowrap;
          padding-left: 0.5rem !important;
          padding-right: 0.5rem !important;
        }

        /* Ensure dropdown items are properly styled */
        .dropdown-item {
          padding: 0.75rem 1rem;
          white-space: nowrap;
          text-align: left;
        }
      }

      /* Additional media query for small tablets */
      @media (min-width: 740px) and (max-width: 900px) {
        /* Further reduce padding for very tight screens */
        .nav-item {
          padding: 0.25rem 0.25rem;
        }
        
        /* Make logo smaller */
        .logo-button {
          font-size: 1.25rem;
          padding: 0.25rem 0.5rem;
        }
        
        /* Make navigation text even smaller */
        .nav-text {
          font-size: 0.85rem;
          padding-left: 0.25rem !important;
          padding-right: 0.25rem !important;
        }
      }
    `;

    // Add the style to the document
    document.head.appendChild(style);

    // Clean up function
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleLogoClick = () => {
    const languagePrefix = getUrlLanguagePrefix(currentLang);
    const fullPath = `${languagePrefix}/news/list/trending`;
    navigate(fullPath);
  };

  // Add responsive screen size state tracking
  const [isMediumScreen, setIsMediumScreen] = useState(false);

  // Add effect to track screen size changes
  useEffect(() => {
    const checkScreenSize = () => {
      // Adjust breakpoint to catch more tablets/iPads
      setIsMediumScreen(window.innerWidth < 1200 && window.innerWidth >= 740);
    };

    // Initial check
    checkScreenSize();

    // Add resize listener
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Add function to toggle submenu in More dropdown
  const toggleMoreSubmenu = (category: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveMoreSubmenu(prev => prev === category ? null : category);
  };

  return (
    <header className="bg-black p-2 shadow-md z-50 relative header-container">
      <div className="container mx-auto flex justify-between items-center px-4">
        {/* Logo */}
        <button
          onClick={handleLogoClick}
          className="text-white text-2xl font-bold hover:text-gray-300 transition-colors flex-shrink-0 logo-button font-serif tracking-tight leading-tight"
          style={{
            color: '#ffffff',
            textShadow: '0px 0px 10px rgba(255,255,255,0.8)'
          }}
        >
          Global24
        </button>

        {/* Mobile Menu Button */}
        <button
          className="text-white md:hidden focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>

        {/* Navigation Menu */}
        <nav
          className={`absolute md:relative top-full left-0 w-full md:w-auto bg-black md:bg-transparent md:flex transition-transform duration-300 nav-menu ${isOpen ? 'block' : 'hidden md:block'
            }`}
          style={{ zIndex: 100 }}
        >
          <ul className="flex flex-col md:flex-row md:space-x-2 lg:space-x-6 p-3 md:p-0 items-center">
            {/* 🔥 Trending with dropdown */}
            <li
              className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
              ref={trendingRef}
            >
              <div
                className="relative group"
                style={{ padding: '0.5rem', margin: '-0.5rem' }}
                onMouseEnter={() => handleMouseEnter('trending')}
                onMouseLeave={() => handleMouseLeave('trending')}
              >
                <button
                  className="nav-text trending-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                  onClick={(e) => {
                    console.log('[HEADER] Trending dropdown clicked');
                    e.preventDefault();
                    e.stopPropagation();

                    const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
                      ('ontouchend' in document || navigator.maxTouchPoints > 0);

                    if ((window.innerWidth < 1024 || isIpadSimulator) && !isTrendingOpen) {
                      toggleDropdown('trending');
                    } else {
                      handleLinkClick('/news/list/trending');
                      setIsTrendingOpen(false);
                    }
                  }}
                >
                  <span style={{ whiteSpace: 'nowrap' }}>{t?.navigations?.header?.trending || 'Trending'}</span>
                  <ChevronDown size={14} className="ml-1" />
                </button>

                {isTrendingOpen && (
                  <>
                    <div className="dropdown-connector"></div>
                    <ul
                      ref={trendingMenuRef}
                      className="trending-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                    >
                      {trendingCategories.map(category => (
                        <li
                          key={category.key}
                          className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                          onClick={() => {
                            handleLinkClick(`/news/list/trending/${category.key}`);
                            toggleDropdown('trending');
                          }}
                        >
                          {category.display}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </li>



            {/* Mid-tier categories - show directly on large screens, in More on medium screens */}
            {!isMediumScreen && (
              <>
                {/* World */}
                <li
                  className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
                  ref={worldRef}
                >
                  <div
                    className="relative group"
                    style={{ padding: '0.5rem', margin: '-0.5rem' }}
                    onMouseEnter={() => handleMouseEnter('world')}
                    onMouseLeave={() => handleMouseLeave('world')}
                  >
                    <button
                      className="nav-text world-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                      onClick={(e) => {
                        console.log('[HEADER] World dropdown clicked');
                        e.preventDefault();
                        e.stopPropagation();

                        const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
                          ('ontouchend' in document || navigator.maxTouchPoints > 0);

                        if ((window.innerWidth < 1024 || isIpadSimulator) && !isWorldOpen) {
                          toggleDropdown('world');
                        } else {
                          handleLinkClick('/news/list/world');
                          setIsWorldOpen(false);
                        }
                      }}
                    >
                      {t?.navigations?.header?.world || 'World'}
                      <ChevronDown size={14} className="ml-1" />
                    </button>

                    {isWorldOpen && (
                      <>
                        <div className="dropdown-connector"></div>
                        <ul
                          ref={worldMenuRef}
                          className="world-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                        >
                          {worldSubtypes.map(category => (
                            <li
                              key={category.key}
                              className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                              onClick={() => {
                                handleLinkClick(`/news/list/world/${category.key}`);
                                toggleDropdown('world');
                              }}
                            >
                              {category.display}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>

                {/* Politics */}
                <li
                  className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
                  ref={politicsRef}
                >
                  <div
                    className="relative group"
                    style={{ padding: '0.5rem', margin: '-0.5rem' }}
                    onMouseEnter={() => handleMouseEnter('politics')}
                    onMouseLeave={() => handleMouseLeave('politics')}
                  >
                    <button
                      className="nav-text politics-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                      onClick={(e) => {
                        console.log('[HEADER] Politics dropdown clicked');
                        e.preventDefault();
                        e.stopPropagation();

                        const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
                          ('ontouchend' in document || navigator.maxTouchPoints > 0);

                        if ((window.innerWidth < 1024 || isIpadSimulator) && !isPoliticsOpen) {
                          toggleDropdown('politics');
                        } else {
                          handleLinkClick('/news/list/politics');
                          setIsPoliticsOpen(false);
                        }
                      }}
                    >
                      {t?.navigations?.header?.politics || 'Politics'}
                      <ChevronDown size={14} className="ml-1" />
                    </button>

                    {isPoliticsOpen && (
                      <>
                        <div className="dropdown-connector"></div>
                        <ul
                          ref={politicsMenuRef}
                          className="politics-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                        >
                          {politicsSubtypes.map(category => (
                            <li
                              key={category.key}
                              className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                              onClick={() => {
                                handleLinkClick(`/news/list/politics/${category.key}`);
                                toggleDropdown('politics');
                              }}
                            >
                              {category.display}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>

                {/* Business */}
                <li
                  className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
                  ref={businessRef}
                >
                  <div
                    className="relative group"
                    style={{ padding: '0.5rem', margin: '-0.5rem' }}
                    onMouseEnter={() => handleMouseEnter('business')}
                    onMouseLeave={() => handleMouseLeave('business')}
                  >
                    <button
                      className="nav-text business-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                      onClick={(e) => {
                        console.log('[HEADER] Business dropdown clicked');
                        e.preventDefault();
                        e.stopPropagation();

                        const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
                          ('ontouchend' in document || navigator.maxTouchPoints > 0);

                        if ((window.innerWidth < 1024 || isIpadSimulator) && !isBusinessOpen) {
                          toggleDropdown('business');
                        } else {
                          handleLinkClick('/news/list/business');
                          setIsBusinessOpen(false);
                        }
                      }}
                    >
                      {t?.navigations?.header?.business || 'Business'}
                      <ChevronDown size={14} className="ml-1" />
                    </button>

                    {isBusinessOpen && (
                      <>
                        <div className="dropdown-connector"></div>
                        <ul
                          ref={businessMenuRef}
                          className="business-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                        >
                          {businessSubtypes.map(category => (
                            <li
                              key={category.key}
                              className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                              onClick={() => {
                                handleLinkClick(`/news/list/business/${category.key}`);
                                toggleDropdown('business');
                              }}
                            >
                              {category.display}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>

                {/* Technology */}
                <li
                  className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
                  ref={technologyRef}
                >
                  <div
                    className="relative group"
                    style={{ padding: '0.5rem', margin: '-0.5rem' }}
                    onMouseEnter={() => handleMouseEnter('technology')}
                    onMouseLeave={() => handleMouseLeave('technology')}
                  >
                    <button
                      className="nav-text technology-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                      onClick={(e) => {
                        console.log('[HEADER] Technology dropdown clicked');
                        e.preventDefault();
                        e.stopPropagation();

                        const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
                          ('ontouchend' in document || navigator.maxTouchPoints > 0);

                        if ((window.innerWidth < 1024 || isIpadSimulator) && !isTechnologyOpen) {
                          toggleDropdown('technology');
                        } else {
                          handleLinkClick('/news/list/technology');
                          setIsTechnologyOpen(false);
                        }
                      }}
                    >
                      {t?.navigations?.header?.technology || 'Technology'}
                      <ChevronDown size={14} className="ml-1" />
                    </button>

                    {isTechnologyOpen && (
                      <>
                        <div className="dropdown-connector"></div>
                        <ul
                          ref={technologyMenuRef}
                          className="technology-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                        >
                          {technologySubtypes.map(category => (
                            <li
                              key={category.key}
                              className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                              onClick={() => {
                                handleLinkClick(`/news/list/technology/${category.key}`);
                                toggleDropdown('technology');
                              }}
                            >
                              {category.display}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>

                {/* Entertainment */}
                <li
                  className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
                  ref={entertainmentRef}
                >
                  <div
                    className="relative group"
                    style={{ padding: '0.5rem', margin: '-0.5rem' }}
                    onMouseEnter={() => handleMouseEnter('entertainment')}
                    onMouseLeave={() => handleMouseLeave('entertainment')}
                  >
                    <button
                      className="nav-text entertainment-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                      onClick={(e) => {
                        console.log('[HEADER] Entertainment dropdown clicked');
                        e.preventDefault();
                        e.stopPropagation();

                        const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
                          ('ontouchend' in document || navigator.maxTouchPoints > 0);

                        if ((window.innerWidth < 1024 || isIpadSimulator) && !isEntertainmentOpen) {
                          toggleDropdown('entertainment');
                        } else {
                          handleLinkClick('/news/list/entertainment');
                          setIsEntertainmentOpen(false);
                        }
                      }}
                    >
                      {t?.navigations?.header?.entertainment || 'Entertainment'}
                      <ChevronDown size={14} className="ml-1" />
                    </button>

                    {isEntertainmentOpen && (
                      <>
                        <div className="dropdown-connector"></div>
                        <ul
                          ref={entertainmentMenuRef}
                          className="entertainment-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                        >
                          {entertainmentCategories.map(category => (
                            <li
                              key={category.key}
                              className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                              onClick={() => {
                                handleLinkClick(`/news/list/entertainment/${category.key}`);
                                toggleDropdown('entertainment');
                              }}
                            >
                              {category.display}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>

                {/* Sports */}
                <li
                  className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
                  ref={sportsRef}
                >
                  <div
                    className="relative group"
                    style={{ padding: '0.5rem', margin: '-0.5rem' }}
                    onMouseEnter={() => handleMouseEnter('sports')}
                    onMouseLeave={() => handleMouseLeave('sports')}
                  >
                    <button
                      className="nav-text sports-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                      onClick={(e) => {
                        console.log('[HEADER] Sports dropdown clicked');
                        e.preventDefault();
                        e.stopPropagation();

                        const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
                          ('ontouchend' in document || navigator.maxTouchPoints > 0);

                        if ((window.innerWidth < 1024 || isIpadSimulator) && !isSportsOpen) {
                          toggleDropdown('sports');
                        } else {
                          handleLinkClick('/news/list/sports');
                          setIsSportsOpen(false);
                        }
                      }}
                    >
                      {t?.navigations?.header?.sports || 'Sports'}
                      <ChevronDown size={14} className="ml-1" />
                    </button>

                    {isSportsOpen && (
                      <>
                        <div className="dropdown-connector"></div>
                        <ul
                          ref={sportsMenuRef}
                          className="sports-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                        >
                          {sportsCategories.map(category => (
                            <li
                              key={category.key}
                              className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                              onClick={() => {
                                handleLinkClick(`/news/list/sports/${category.key}`);
                                toggleDropdown('sports');
                              }}
                            >
                              {category.display}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>

                {/* Health */}
                <li
                  className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
                  ref={healthRef}
                >
                  <div
                    className="relative group"
                    style={{ padding: '0.5rem', margin: '-0.5rem' }}
                    onMouseEnter={() => handleMouseEnter('health')}
                    onMouseLeave={() => handleMouseLeave('health')}
                  >
                    <button
                      className="nav-text health-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                      onClick={(e) => {
                        console.log('[HEADER] Health dropdown clicked');
                        e.preventDefault();
                        e.stopPropagation();

                        const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
                          ('ontouchend' in document || navigator.maxTouchPoints > 0);

                        if ((window.innerWidth < 1024 || isIpadSimulator) && !isHealthOpen) {
                          toggleDropdown('health');
                        } else {
                          handleLinkClick('/news/list/health');
                          setIsHealthOpen(false);
                        }
                      }}
                    >
                      {t?.navigations?.header?.health || 'Health'}
                      <ChevronDown size={14} className="ml-1" />
                    </button>

                    {isHealthOpen && (
                      <>
                        <div className="dropdown-connector"></div>
                        <ul
                          ref={healthMenuRef}
                          className="health-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                        >
                          {healthSubtypes.map(category => (
                            <li
                              key={category.key}
                              className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                              onClick={() => {
                                handleLinkClick(`/news/list/health/${category.key}`);
                                toggleDropdown('health');
                              }}
                            >
                              {category.display}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>

                {/* Lifestyle */}
                <li
                  className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
                  ref={lifestyleRef}
                >
                  <div
                    className="relative group"
                    style={{ padding: '0.5rem', margin: '-0.5rem' }}
                    onMouseEnter={() => handleMouseEnter('lifestyle')}
                    onMouseLeave={() => handleMouseLeave('lifestyle')}
                  >
                    <button
                      className="nav-text lifestyle-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                      onClick={(e) => {
                        console.log('[HEADER] Lifestyle dropdown clicked');
                        e.preventDefault();
                        e.stopPropagation();

                        const isIpadSimulator = /iPad|Macintosh/i.test(navigator.userAgent) &&
                          ('ontouchend' in document || navigator.maxTouchPoints > 0);

                        if ((window.innerWidth < 1024 || isIpadSimulator) && !isLifestyleOpen) {
                          toggleDropdown('lifestyle');
                        } else {
                          handleLinkClick('/news/list/lifestyle');
                          setIsLifestyleOpen(false);
                        }
                      }}
                    >
                      {t?.navigations?.header?.lifestyle || 'Lifestyle'}
                      <ChevronDown size={14} className="ml-1" />
                    </button>

                    {isLifestyleOpen && (
                      <>
                        <div className="dropdown-connector"></div>
                        <ul
                          ref={lifestyleMenuRef}
                          className="lifestyle-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                        >
                          {lifestyleSubtypes.map(category => (
                            <li
                              key={category.key}
                              className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                              onClick={() => {
                                handleLinkClick(`/news/list/lifestyle/${category.key}`);
                                toggleDropdown('lifestyle');
                              }}
                            >
                              {category.display}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>
              </>
            )}

            {/* More dropdown for medium screens */}
            {isMediumScreen && (
              <li
                className="nav-item p-1 md:p-0 text-white hover:text-gray-300 transition-colors relative"
                ref={moreRef}
              >
                <div
                  className="relative group"
                  style={{ padding: '0.5rem', margin: '-0.5rem' }}
                  onMouseEnter={() => handleMouseEnter('more')}
                  onMouseLeave={() => handleMouseLeave('more')}
                >
                  <button
                    className="nav-text more-dropdown text-white hover:text-gray-300 px-3 py-2 rounded flex items-center font-semibold text-lg font-serif tracking-tight leading-tight"
                    onClick={(e) => {
                      console.log('[HEADER] More dropdown clicked');
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDropdown('more');
                    }}
                  >
                    {t?.navigations?.header?.more || 'More'}
                    <ChevronDown size={14} className="ml-1" />
                  </button>

                  {isMoreOpen && (
                    <>
                      <div className="dropdown-connector"></div>
                      <ul
                        ref={moreMenuRef}
                        className="more-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-64 md:w-72"
                      >
                        {/* World with subtypes */}
                        <li className="border-b border-gray-700">
                          <div
                            className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base font-medium flex justify-between items-center"
                            onClick={(e) => toggleMoreSubmenu('world', e)}
                          >
                            <span>{t?.navigations?.header?.world || 'World'}</span>
                            <ChevronDown
                              size={14}
                              className={`transform transition-transform ${activeMoreSubmenu === 'world' ? 'rotate-180' : ''}`}
                            />
                          </div>
                          {activeMoreSubmenu === 'world' && (
                            <div className="pl-4 pb-2 bg-gray-900">
                              <div
                                className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                onClick={() => {
                                  handleLinkClick ('/news/list/world');
                                  toggleDropdown('more');
                                }}
                              >
                                {t?.navigations?.header?.all || 'All World News'}
                              </div>
                              {worldSubtypes.map(subtype => (
                                <div
                                  key={subtype.key}
                                  className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                  onClick={() => {
                                    handleLinkClick(`/news/list/world/${subtype.key}`);
                                    toggleDropdown('more');
                                  }}
                                >
                                  {subtype.display}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>

                        {/* Politics with subtypes */}
                        <li className="border-b border-gray-700">
                          <div
                            className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base font-medium flex justify-between items-center"
                            onClick={(e) => toggleMoreSubmenu('politics', e)}
                          >
                            <span>{t?.navigations?.header?.politics || 'Politics'}</span>
                            <ChevronDown
                              size={14}
                              className={`transform transition-transform ${activeMoreSubmenu === 'politics' ? 'rotate-180' : ''}`}
                            />
                          </div>
                          {activeMoreSubmenu === 'politics' && (
                            <div className="pl-4 pb-2 bg-gray-900">
                              <div
                                className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                onClick={() => {
                                  handleLinkClick('/news/list/politics');
                                  toggleDropdown('more');
                                }}
                              >
                                {t?.navigations?.header?.all || 'All Politics News'}
                              </div>
                              {politicsSubtypes.map(subtype => (
                                <div
                                  key={subtype.key}
                                  className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                  onClick={() => {
                                    handleLinkClick(`/news/list/politics/${subtype.key}`);
                                    toggleDropdown('more');
                                  }}
                                >
                                  {subtype.display}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>

                        {/* Business with subtypes */}
                        <li className="border-b border-gray-700">
                          <div
                            className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base font-medium flex justify-between items-center"
                            onClick={(e) => toggleMoreSubmenu('business', e)}
                          >
                            <span>{t?.navigations?.header?.business || 'Business'}</span>
                            <ChevronDown
                              size={14}
                              className={`transform transition-transform ${activeMoreSubmenu === 'business' ? 'rotate-180' : ''}`}
                            />
                          </div>
                          {activeMoreSubmenu === 'business' && (
                            <div className="pl-4 pb-2 bg-gray-900">
                              <div
                                className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                onClick={() => {
                                  handleLinkClick('/news/list/business');
                                  toggleDropdown('more');
                                }}
                              >
                                {t?.navigations?.header?.all || 'All Business News'}
                              </div>
                              {businessSubtypes.map(subtype => (
                                <div
                                  key={subtype.key}
                                  className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                  onClick={() => {
                                    handleLinkClick(`/news/list/business/${subtype.key}`);
                                    toggleDropdown('more');
                                  }}
                                >
                                  {subtype.display}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>

                        {/* Technology with subtypes */}
                        <li className="border-b border-gray-700">
                          <div
                            className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base font-medium flex justify-between items-center"
                            onClick={(e) => toggleMoreSubmenu('technology', e)}
                          >
                            <span>{t?.navigations?.header?.technology || 'Technology'}</span>
                            <ChevronDown
                              size={14}
                              className={`transform transition-transform ${activeMoreSubmenu === 'technology' ? 'rotate-180' : ''}`}
                            />
                          </div>
                          {activeMoreSubmenu === 'technology' && (
                            <div className="pl-4 pb-2 bg-gray-900">
                              <div
                                className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                onClick={() => {
                                  handleLinkClick('/news/list/technology');
                                  toggleDropdown('more');
                                }}
                              >
                                {t?.navigations?.header?.all || 'All Technology News'}
                              </div>
                              {technologySubtypes.map(subtype => (
                                <div
                                  key={subtype.key}
                                  className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                  onClick={() => {
                                    handleLinkClick(`/news/list/technology/${subtype.key}`);
                                    toggleDropdown('more');
                                  }}
                                >
                                  {subtype.display}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>

                        {/* Health with subtypes */}
                        <li className="border-b border-gray-700">
                          <div
                            className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base font-medium flex justify-between items-center"
                            onClick={(e) => toggleMoreSubmenu('health', e)}
                          >
                            <span>{t?.navigations?.header?.health || 'Health'}</span>
                            <ChevronDown
                              size={14}
                              className={`transform transition-transform ${activeMoreSubmenu === 'health' ? 'rotate-180' : ''}`}
                            />
                          </div>
                          {activeMoreSubmenu === 'health' && (
                            <div className="pl-4 pb-2 bg-gray-900">
                              <div
                                className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                onClick={() => {
                                  handleLinkClick('/news/list/health');
                                  toggleDropdown('more');
                                }}
                              >
                                {t?.navigations?.header?.all || 'All Health News'}
                              </div>
                              {healthSubtypes.map(subtype => (
                                <div
                                  key={subtype.key}
                                  className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                  onClick={() => {
                                    handleLinkClick(`/news/list/health/${subtype.key}`);
                                    toggleDropdown('more');
                                  }}
                                >
                                  {subtype.display}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>

                        {/* Lifestyle with subtypes */}
                        <li>
                          <div
                            className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base font-medium flex justify-between items-center"
                            onClick={(e) => toggleMoreSubmenu('lifestyle', e)}
                          >
                            <span>{t?.navigations?.header?.lifestyle || 'Lifestyle'}</span>
                            <ChevronDown
                              size={14}
                              className={`transform transition-transform ${activeMoreSubmenu === 'lifestyle' ? 'rotate-180' : ''}`}
                            />
                          </div>
                          {activeMoreSubmenu === 'lifestyle' && (
                            <div className="pl-4 pb-2 bg-gray-900">
                              <div
                                className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                onClick={() => {
                                  handleLinkClick('/news/list/lifestyle');
                                  toggleDropdown('more');
                                }}
                              >
                                {t?.navigations?.header?.all || 'All Lifestyle News'}
                              </div>
                              {lifestyleSubtypes.map(subtype => (
                                <div
                                  key={subtype.key}
                                  className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm"
                                  onClick={() => {
                                    handleLinkClick(`/news/list/lifestyle/${subtype.key}`);
                                    toggleDropdown('more');
                                  }}
                                >
                                  {subtype.display}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>
                      </ul>
                    </>
                  )}
                </div>
              </li>
            )}

            <li
              className="nav-item p-1 md:p-0 font-bold text-white hover:text-gray-300 transition-colors relative ml-auto"
              ref={languageRef}
            >
              <div
                className="relative group"
                style={{ padding: '0.5rem', margin: '-0.5rem' }}
                onMouseEnter={() => handleMouseEnter('language')}
                onMouseLeave={() => handleMouseLeave('language')}
              >
                <button
                  className="nav-text language-dropdown hover:text-gray-300 px-2 py-1 rounded flex items-center font-serif tracking-tight leading-tight"
                  style={{ color: '#42a5f5' }}
                  onClick={(e) => {
                    console.log('[HEADER] Language dropdown clicked');
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDropdown('language');
                  }}
                >
                  <span className="hidden md:block lg:hidden">{currentLanguage.code.split('-')[0].toUpperCase()}</span>
                  <span className="hidden lg:block">{currentLanguage.display}</span>
                  <span className="block md:hidden">{currentLanguage.display}</span>
                  <ChevronDown size={14} className="ml-1" />
                </button>

                {isLanguageOpen && (
                  <>
                    <div className="dropdown-connector"></div>
                    <ul
                      ref={languageMenuRef}
                      className="language-dropdown-menu absolute z-10 bg-black border border-gray-700 mt-1 rounded-md w-36 md:w-48"
                    >
                      {filteredLanguages.length > 0 ? (
                        filteredLanguages.map(lang => (
                          <li
                            key={lang.code}
                            className="dropdown-item hover:bg-gray-700 px-3 py-2 cursor-pointer text-sm md:text-base"
                            onClick={() => {
                              handleLanguageSwitch(lang.code);
                              toggleDropdown('language');
                            }}
                          >
                            {lang.display}
                          </li>
                        ))
                      ) : (
                        <li className="dropdown-item px-3 py-2 text-gray-400">No other languages</li>
                      )}
                    </ul>
                  </>
                )}
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;